import type { ActionDescriptor, ActionEffect } from '@krp/client-runtime'

import { SCHEDULE_WINDOWS as W } from '../windows/registerWindows'

/**
 * Реестр действий пакета `schedule-ui` (UI-ACTIONS): что делает каждая кнопка раздела «График выхода» — для подсказки ⓘ.
 * Описания — словами магазина, без кодов и внутренних слов (проверяет тест реестра приложения). Статус не пишется руками:
 * он вычисляется из доказательств, а их у раздела ещё нет — тесты и живая проверка придут вместе со службой на стенде.
 */
const VIEW = 'Тот, кому выдано право смотреть график выхода.'
const MANAGE = 'Тот, кому выдано право править график выхода.'
const SAVED = 'Сохраняется на сервере: после обновления страницы, на другом устройстве и у других сотрудников график такой же.'
const ONLY_SCREEN = 'Меняет только то, что видно на экране; ничего не сохраняет.'
const RUNNING = 'Команда выполняется — дождитесь ответа.'

interface Entry {
  id: string
  windows: readonly string[]
  label: string
  summary: string
  effect: ActionEffect
  effectText?: string
  access?: string
  disabledWhen?: string
  gestureOf?: string
}

const entry = (value: Entry): ActionDescriptor => ({ ...value, evidence: {} })

export const scheduleActions: readonly ActionDescriptor[] = [
  // Вход раздела: плитки объектов
  entry({
    id: 'schedule.home.create-place',
    windows: [W.home],
    label: 'Создать объект',
    summary: 'Открывает форму нового объекта: магазина, точки или любого места, где работают кассиры. Название вольное, например «Волная 28». Объект появится плиткой после «Создать».',
    effect: 'view',
    effectText: ONLY_SCREEN,
    access: MANAGE,
  }),
  entry({ id: 'schedule.home.open-place', windows: [W.home], label: 'Открыть график объекта', summary: 'Открывает график этого объекта за месяц: слева сотрудники объекта, сверху дни, в клетках — кто выходит.', effect: 'view', access: VIEW }),
  entry({ id: 'schedule.home.edit-place', windows: [W.home], label: 'Изменить объект', summary: 'Открывает форму объекта: переименовать его или убрать в архив.', effect: 'view', effectText: ONLY_SCREEN, access: MANAGE }),
  entry({
    id: 'schedule.home.show-cashiers',
    windows: [W.home],
    label: 'По кассирам',
    summary: 'Открывает раздел «По кассирам»: выберите кассира и увидите его месяц по всем объектам — где он стоит в какие дни, сколько дней набралось.',
    effect: 'view',
    effectText: ONLY_SCREEN,
    access: VIEW,
  }),
  entry({ id: 'schedule.home.reload', windows: [W.home], label: 'Повторить', summary: 'Объекты не пришли. Кнопка запрашивает их ещё раз.', effect: 'read', effectText: 'Только перечитывает.' }),

  // График объекта
  entry({ id: 'schedule.place.previous-month', windows: [W.place], label: 'Предыдущий месяц', summary: 'Показывает график объекта за предыдущий месяц.', effect: 'view', effectText: ONLY_SCREEN, access: VIEW }),
  entry({ id: 'schedule.place.next-month', windows: [W.place], label: 'Следующий месяц', summary: 'Показывает график объекта за следующий месяц.', effect: 'view', effectText: ONLY_SCREEN, access: VIEW }),
  entry({ id: 'schedule.place.today', windows: [W.place], label: 'Текущий месяц', summary: 'Возвращает график объекта к месяцу, в котором сегодняшний день.', effect: 'view', effectText: ONLY_SCREEN, access: VIEW }),
  entry({
    id: 'schedule.place.add-member',
    windows: [W.place],
    label: 'Добавить сотрудника',
    summary: 'Открывает небольшое окно со списком кассиров, которых ещё нет в составе объекта: поиск по имени, можно отметить нескольких. Пока вы не нажали «Добавить», ничего не меняется.',
    effect: 'view',
    effectText: ONLY_SCREEN,
    access: MANAGE,
    disabledWhen: 'Объект в архиве или нет права править график.',
  }),
  entry({
    id: 'schedule.place.add-members-confirm',
    windows: [W.place],
    label: 'Добавить',
    summary:
      'Добавляет отмеченных кассиров в состав объекта: каждый появляется в таблице строкой и остаётся в составе, пока его не уберут. Если кому-то добавить не удалось, окно остаётся открытым и показывает причину; уже добавленные повторно не добавляются.',
    effect: 'write-local',
    effectText: SAVED,
    access: MANAGE,
    disabledWhen: 'Никто не отмечен или добавление уже идёт.',
  }),
  entry({ id: 'schedule.place.add-members-cancel', windows: [W.place], label: 'Отмена', summary: 'Закрывает окно без добавления: состав объекта остаётся как был.', effect: 'view', effectText: ONLY_SCREEN, access: MANAGE, disabledWhen: RUNNING }),
  entry({
    id: 'schedule.place.remove-member',
    windows: [W.place],
    label: 'Убрать из состава',
    summary: 'Открывает вопрос «Убрать кассира с объекта?». Пока вы не подтвердили, ничего не меняется.',
    effect: 'view',
    effectText: ONLY_SCREEN,
    access: MANAGE,
  }),
  entry({
    id: 'schedule.place.remove-member-confirm',
    windows: [W.place],
    label: 'Убрать',
    summary: 'Убирает кассира с объекта: его строка исчезает из состава, а выходы на этом объекте, начиная с сегодняшнего дня, снимаются. Прошлые дни сохраняются в истории графика.',
    effect: 'write-local',
    effectText: SAVED,
    access: MANAGE,
  }),
  entry({ id: 'schedule.place.remove-member-cancel', windows: [W.place], label: 'Не убирать', summary: 'Отказывается от снятия: кассир и его выходы остаются как были.', effect: 'view', effectText: ONLY_SCREEN, access: MANAGE }),
  entry({ id: 'schedule.place.reload', windows: [W.place], label: 'Повторить', summary: 'График не пришёл. Кнопка запрашивает его ещё раз.', effect: 'read', effectText: 'Только перечитывает.' }),

  // Клетка таблицы графика (общая у объекта и «По кассирам»)
  entry({
    id: 'schedule.grid.toggle-cell',
    windows: [W.place, W.cashiers],
    label: 'Отметка в клетке',
    summary:
      'Клетка таблицы — кассир на объекте в этот день. Левый клик ставит «Р» (кассир выходит), если там другая отметка или клетка не отмечена, и очищает клетку, если «Р» уже стоит; клетка с «Р» закрашивается цветом кассира. Правый клик по клетке (на телефоне — долгое нажатие, с клавиатуры — клавиша меню или Shift+F10) открывает меню: «Р — Выходит», «В — Выходной день», «Н — Не выходит» и «Очистить». До ответа сервера в клетке крутится загрузчик; если сервер отказал, клетка возвращается как была, а причина показана над таблицей. Не отмеченная клетка, где кассир в этот день уже выходит на другом объекте, выглядит как «Н», при наведении в ней появляется замок и подсказка с названием объекта: «Р» туда не ставится — ничего не отправляется, клетка коротко вспыхивает красным, а рядом появляется подсказка «Нельзя: день занят — объект «…»» (так же — при отказе сервера «день занят»; панели ошибки в этом случае нет). «В» и «Н» ставятся всегда.',
    effect: 'write-local',
    effectText: SAVED,
    access: MANAGE,
    disabledWhen: 'Объект в архиве или нет права править график; пока по клетке идёт команда, повторное нажатие ничего не делает.',
  }),
  entry({
    id: 'schedule.grid.menu-work',
    windows: [W.place, W.cashiers],
    label: 'Р — Выходит',
    summary:
      'Отмечает в клетке «Р»: кассир выходит на объект в этот день. Клетка закрашивается цветом кассира, день попадает в «Всего». Если в этот день кассир уже выходит на другом объекте, ничего не отправляется: клетка коротко вспыхивает красным, а рядом появляется подсказка с названием объекта.',
    effect: 'write-local',
    effectText: SAVED,
    access: MANAGE,
    disabledWhen: 'Объект в архиве или нет права править график; пока по клетке идёт команда, выбор ничего не делает.',
  }),
  entry({
    id: 'schedule.grid.menu-day-off',
    windows: [W.place, W.cashiers],
    label: 'В — Выходной день',
    summary:
      'Отмечает в клетке «В»: у кассира в этот день выходной. Клетка получает холодный тон со штриховкой. В «Всего» такой день не считается, а на другом объекте кассира в этот день поставить можно.',
    effect: 'write-local',
    effectText: SAVED,
    access: MANAGE,
    disabledWhen: 'Объект в архиве или нет права править график; пока по клетке идёт команда, выбор ничего не делает.',
  }),
  entry({
    id: 'schedule.grid.menu-absent',
    windows: [W.place, W.cashiers],
    label: 'Н — Не выходит',
    summary:
      'Отмечает в клетке «Н»: кассир в этот день не выходит — это явная отметка, а не пустая клетка. В «Всего» такой день не считается, а на другом объекте кассира в этот день поставить можно.',
    effect: 'write-local',
    effectText: SAVED,
    access: MANAGE,
    disabledWhen: 'Объект в архиве или нет права править график; пока по клетке идёт команда, выбор ничего не делает.',
  }),
  entry({
    id: 'schedule.grid.menu-clear',
    windows: [W.place, W.cashiers],
    label: 'Очистить',
    summary: 'Убирает отметку из клетки, какой бы она ни была («Р», «В» или «Н»): клетка снова не отмечена. Пункт есть только у клетки, в которой что-то отмечено.',
    effect: 'write-local',
    effectText: SAVED,
    access: MANAGE,
    disabledWhen: 'Объект в архиве или нет права править график; пока по клетке идёт команда, выбор ничего не делает.',
  }),

  // По кассирам
  entry({ id: 'schedule.cashiers.show-places', windows: [W.cashiers], label: 'Объекты', summary: 'Возвращает к плиткам объектов графика.', effect: 'view', effectText: ONLY_SCREEN, access: VIEW }),
  entry({ id: 'schedule.cashiers.previous-month', windows: [W.cashiers], label: 'Предыдущий месяц', summary: 'Показывает график выбранного кассира за предыдущий месяц.', effect: 'view', effectText: ONLY_SCREEN, access: VIEW }),
  entry({ id: 'schedule.cashiers.next-month', windows: [W.cashiers], label: 'Следующий месяц', summary: 'Показывает график выбранного кассира за следующий месяц.', effect: 'view', effectText: ONLY_SCREEN, access: VIEW }),
  entry({ id: 'schedule.cashiers.today', windows: [W.cashiers], label: 'Текущий месяц', summary: 'Возвращает таблицу к месяцу, в котором сегодняшний день.', effect: 'view', effectText: ONLY_SCREEN, access: VIEW }),
  entry({ id: 'schedule.cashiers.reload', windows: [W.cashiers], label: 'Повторить', summary: 'График по кассирам не пришёл. Кнопка запрашивает его ещё раз.', effect: 'read', effectText: 'Только перечитывает.' }),

  // Новый объект и правка объекта
  entry({
    id: 'schedule.place-form.save',
    windows: [W.newPlace, W.editPlace],
    label: 'Сохранить',
    summary: 'Записывает объект: создаёт новый или меняет название существующего. Название не должно повторять другой объект — иначе график скажет об этом.',
    effect: 'write-local',
    effectText: SAVED,
    access: MANAGE,
    disabledWhen: 'Название пустое, слишком длинное, не менялось или команда уже выполняется.',
  }),
  entry({ id: 'schedule.place-form.archive', windows: [W.editPlace], label: 'В архив', summary: 'Предлагает убрать объект в архив. Пока вы не подтвердили, ничего не меняется.', effect: 'view', effectText: ONLY_SCREEN, access: MANAGE, disabledWhen: RUNNING }),
  entry({
    id: 'schedule.place-form.archive-confirm',
    windows: [W.editPlace],
    label: 'Да, убрать в архив',
    summary: 'Убирает объект из списка плиток. Назначенные кассиры в календаре сохраняются, но менять их станет нельзя.',
    effect: 'write-local',
    effectText: SAVED,
    access: MANAGE,
    disabledWhen: RUNNING,
  }),
  entry({ id: 'schedule.place-form.archive-cancel', windows: [W.editPlace], label: 'Не убирать', summary: 'Отказывается от архива: объект остаётся как был.', effect: 'view', effectText: ONLY_SCREEN, access: MANAGE, disabledWhen: RUNNING }),
  entry({ id: 'schedule.place-form.close', windows: [W.newPlace, W.editPlace], label: 'Закрыть', summary: 'Закрывает форму объекта без сохранения.', effect: 'view', effectText: ONLY_SCREEN, access: MANAGE, disabledWhen: RUNNING }),
  entry({ id: 'schedule.place-form.reload', windows: [W.newPlace, W.editPlace], label: 'Повторить', summary: 'Объект не пришёл. Кнопка запрашивает его ещё раз.', effect: 'read', effectText: 'Только перечитывает.' }),
]
